import { readSseJson } from '@/lib/sse'
import { replaceUrl } from '@/lib/utils'
import type { DataFile } from '@/types'
import type { ChatAttachment, ChatMessage } from '@/types/chat'
import type { ChatMessageCreateRequest, ChatSessionCreateRequest, EditConversationRequest } from '@/types/request'
import type {
  ConversationResponse,
  EditConversationResponse,
  RoomResponse,
  SessionDetailResponse,
  SessionResponse,
} from '@/types/response'
import { UseQueryResult } from '@tanstack/react-query'
import { create } from 'zustand'

interface RegenerateMessageParams {
  conversationId: number
  modelVersionId: number | null
  agentId: number | null
  regenerateMessage: (params: {
    pk: number
    model_version_id?: number | null
    agent_id?: number | null
  }) => Promise<Response>
  onComplete?: () => void
}

export interface SelectedChatFile {
  id: string
  file: File
  name: string
}

interface SendMessageParams {
  message: string
  modelVersionId: number | null
  agentId: number | null
  agentName: string | null
  effortLevel: string | null
  createSession: (data: ChatSessionCreateRequest) => Promise<SessionDetailResponse>
  sendMessage: (data: ChatMessageCreateRequest) => Promise<Response>
  fetchSession: (sessionId: number, branchId: string | null) => Promise<SessionDetailResponse>
  toRefreshQueries: Array<UseQueryResult>
}

interface RetryMessageParams {
  conversationId: number
  retryMessage: (params: { pk: number }) => Promise<Response>
}

function getStreamDelta(data: unknown) {
  if (typeof data !== 'object' || data === null) return ''
  const delta = (data as { delta?: unknown }).delta
  return typeof delta === 'string' ? delta : ''
}

function readConversationStream<T>(response: Response, onUpdate: (delta: string, label?: string) => void) {
  return readSseJson<T>(response, {
    handlers: {
      chunk: (data) => {
        const delta = getStreamDelta(data)
        if (delta) onUpdate(delta)
      },
      progress: (data) => {
        if (typeof data !== 'object' || data === null) return
        const label = (data as { label?: unknown }).label
        if (typeof label === 'string') onUpdate('', label)
      },
    },
  })
}

interface EditMessageParams {
  conversationId: number
  content: string
  editMessage: (params: { pk: number; data: EditConversationRequest }) => Promise<Response>
  onComplete?: () => void
}

function createPendingAttachments(uploadedFiles: DataFile[], selectedFiles: SelectedChatFile[]) {
  let tempId = -Date.now()
  const attachmentFiles = [
    ...uploadedFiles.map((file) => ({ id: file.id, name: file.name })),
    ...selectedFiles.map((file) => ({ id: tempId--, name: file.name, localFile: file.file })),
  ]

  return attachmentFiles.map<ChatAttachment>((file) => ({ id: tempId--, role: 'question', file }))
}

interface ChatState {
  session: SessionDetailResponse | null
  conversations: ChatMessage[]
  activeBranchId: string | null
  currentRoom: RoomResponse | null
  sessionAccessRevoked: boolean
  selectedFiles: SelectedChatFile[]
  uploadedFiles: DataFile[]
  selectSession: (session: SessionDetailResponse) => void
  setSessionDetail: (session: SessionDetailResponse, conversations: ConversationResponse[]) => void
  renameSession: (session: SessionResponse) => void
  createTempSession: () => void
  setCurrentRoom: (room: RoomResponse | null) => void
  setSessionAccessRevoked: (value: boolean) => void
  addSelectedFiles: (files: File[]) => void
  removeSelectedFile: (id: string) => void
  clearSelectedFiles: () => void
  setUploadedFiles: (files: DataFile[]) => void
  sendMessage: (params: SendMessageParams) => Promise<void>
  retryMessage: (params: RetryMessageParams) => Promise<void>
  editMessage: (params: EditMessageParams) => Promise<void>
  regenerateMessage: (params: RegenerateMessageParams) => Promise<void>
  switchBranch: (branchId: string) => void
  setConversationFeedback: (conversationId: number, feedback: boolean | null) => void
}

export const useChatStore = create<ChatState>()((set, get) => ({
  session: null,
  conversations: [],
  activeBranchId: null,
  currentRoom: null,
  sessionAccessRevoked: false,
  selectedFiles: [],
  uploadedFiles: [],

  selectSession: (session) =>
    set({ session, conversations: [], activeBranchId: null, selectedFiles: [], uploadedFiles: [] }),
  setSessionDetail: (session, conversations) => set({ session, conversations }),
  setCurrentRoom: (room) => set({ currentRoom: room }),
  setSessionAccessRevoked: (value) => set({ sessionAccessRevoked: value }),
  renameSession: (session) =>
    set((state) => ({
      session: state.session?.id === session.id ? { ...state.session, ...session } : state.session,
    })),

  createTempSession: () =>
    set({ session: null, conversations: [], activeBranchId: null, selectedFiles: [], uploadedFiles: [] }),
  addSelectedFiles: (files) =>
    set((state) => ({
      selectedFiles: [
        ...state.selectedFiles,
        ...files.map((file) => ({
          id: crypto.randomUUID(),
          file,
          name: file.name,
        })),
      ],
    })),
  removeSelectedFile: (id) =>
    set((state) => ({
      selectedFiles: state.selectedFiles.filter((file) => file.id !== id),
    })),
  clearSelectedFiles: () => set({ selectedFiles: [] }),
  setUploadedFiles: (files) => set({ uploadedFiles: files }),
  sendMessage: async ({
    message,
    modelVersionId,
    agentId,
    agentName,
    effortLevel,
    createSession,
    sendMessage,
    fetchSession,
    toRefreshQueries,
  }) => {
    const content = message.trim()
    if (!content) return

    const selectedFiles = get().selectedFiles
    const uploadedFiles = get().uploadedFiles
    let session = get().session

    if (session === null) {
      session = await createSession({ agent_id: agentId, room_id: get().currentRoom?.uuid ?? null })
      set({ session })
      replaceUrl(`/c/${session.id}`)
    }

    const sessionAgent = session.agent
    const targetAgentId = sessionAgent?.id ?? agentId
    const targetAgentName = sessionAgent?.name ?? agentName
    const targetModelVersionId = sessionAgent ? null : modelVersionId

    const pendingConversation: ChatMessage = {
      id: -Date.now(),
      content,
      reply: '',
      type: 'chat',
      parent_id: null,
      version_no: 1,
      version_root_id: null,
      total_versions: 1,
      model_version_name: null,
      agent_id: targetAgentId,
      agent_name: targetAgentName,
      feedback: null,
      status: 'success',
      created: new Date().toISOString(),
      attachments: createPendingAttachments(uploadedFiles, selectedFiles),
    }

    set((state) => ({
      conversations: [...state.conversations, pendingConversation],
    }))

    try {
      const response = await sendMessage({
        session_id: session.id,
        content,
        branch_id: get().activeBranchId,
        model_version_id: targetModelVersionId,
        agent_id: targetAgentId,
        effort_level: effortLevel,
        upload_documents: selectedFiles.map((file) => ({ file: file.file, name: file.name })),
      })
      const conversation = await readConversationStream<ConversationResponse>(response, (delta, label) => {
        set((state) => ({
          conversations: state.conversations.map((item) =>
            item.id === pendingConversation.id ? { ...item, reply: item.reply + delta, event: label } : item,
          ),
        }))
      })

      set((state) => ({
        session: state.session
          ? {
              ...state.session,
              name: state.session.name || content.slice(0, 32),
              modified: conversation.created,
            }
          : state.session,
        conversations: state.conversations.map((item) => (item.id === pendingConversation.id ? conversation : item)),
        selectedFiles: [],
      }))
    } catch (error) {
      try {
        const sessionDetail = await fetchSession(session.id, get().activeBranchId)
        set({ session: sessionDetail, conversations: sessionDetail.conversations })
      } catch {
        set((state) => ({
          conversations: state.conversations.map((item) =>
            item.id === pendingConversation.id ? { ...item, status: 'error', event: undefined } : item,
          ),
        }))
      }
      throw error
    } finally {
      await Promise.all(toRefreshQueries.map((toRefreshQuery) => toRefreshQuery.refetch()))
    }
  },

  retryMessage: async ({ conversationId, retryMessage }) => {
    set((state) => ({
      conversations: state.conversations.map((conversation) =>
        conversation.id === conversationId
          ? { ...conversation, reply: '', status: 'success', event: undefined }
          : conversation,
      ),
    }))

    try {
      const response = await retryMessage({ pk: conversationId })
      const conversation = await readConversationStream<ConversationResponse>(response, (delta, label) => {
        set((state) => ({
          conversations: state.conversations.map((item) =>
            item.id === conversationId ? { ...item, reply: item.reply + delta, event: label } : item,
          ),
        }))
      })
      set((state) => ({
        conversations: state.conversations.map((item) => (item.id === conversationId ? conversation : item)),
      }))
    } catch (error) {
      set((state) => ({
        conversations: state.conversations.map((conversation) =>
          conversation.id === conversationId ? { ...conversation, status: 'error', event: undefined } : conversation,
        ),
      }))
      throw error
    }
  },

  editMessage: async ({ conversationId, content, editMessage, onComplete }) => {
    const previousConversations = get().conversations
    const editedIndex = previousConversations.findIndex((conversation) => conversation.id === conversationId)
    const editedConversation = previousConversations[editedIndex]
    const pendingConversationId = -Date.now()

    if (editedConversation) {
      set({
        conversations: [
          ...previousConversations.slice(0, editedIndex),
          { ...editedConversation, id: pendingConversationId, content, reply: '', status: 'success', event: undefined },
        ],
      })
    }

    try {
      const response = await editMessage({ pk: conversationId, data: { content } })
      const result = await readConversationStream<EditConversationResponse>(response, (delta, label) => {
        set((state) => ({
          conversations: state.conversations.map((conversation) =>
            conversation.id === pendingConversationId
              ? { ...conversation, reply: conversation.reply + delta, event: label }
              : conversation,
          ),
        }))
      })
      set({
        activeBranchId: result.branch_id,
        conversations: [...previousConversations.slice(0, Math.max(editedIndex, 0)), result.conversation],
      })
      onComplete?.()
    } catch (error) {
      set({ conversations: previousConversations })
      throw error
    }
  },

  regenerateMessage: async ({ conversationId, modelVersionId, agentId, regenerateMessage, onComplete }) => {
    const pendingConversationId = -Date.now()
    const previousConversations = get().conversations
    const replacedIndex = previousConversations.findIndex((conversation) => conversation.id === conversationId)
    const replacedConversation = previousConversations[replacedIndex]

    if (replacedConversation) {
      set({
        conversations: [
          ...previousConversations.slice(0, replacedIndex),
          { ...replacedConversation, id: pendingConversationId, reply: '', status: 'success', event: undefined },
        ],
      })
    }

    try {
      const response = await regenerateMessage({
        pk: conversationId,
        model_version_id: modelVersionId,
        agent_id: agentId,
      })
      const result = await readConversationStream<EditConversationResponse>(response, (delta, label) => {
        set((state) => ({
          conversations: state.conversations.map((conversation) =>
            conversation.id === pendingConversationId
              ? { ...conversation, reply: conversation.reply + delta, event: label }
              : conversation,
          ),
        }))
      })
      set({
        activeBranchId: result.branch_id,
        conversations: [...previousConversations.slice(0, Math.max(replacedIndex, 0)), result.conversation],
      })
      onComplete?.()
    } catch (error) {
      set({ conversations: previousConversations })
      throw error
    }
  },

  switchBranch: (branchId) => set({ activeBranchId: branchId }),

  setConversationFeedback: (conversationId, feedback) =>
    set((state) => ({
      conversations: state.conversations.map((c) => (c.id === conversationId ? { ...c, feedback } : c)),
    })),
}))
